import { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import axios from "axios";
import {
    Users, Calendar, CheckCircle2, Activity, ClipboardList, Briefcase,
    Layers, Megaphone, CreditCard, DollarSign, MessageCircle, UserCog,
    BarChart3, LogOut, ChevronLeft, ChevronRight, ChevronDown, Loader2, MessageSquare, Tag, Scale, UserPlus, ArrowRightLeft, TrendingUp, Search, ShieldCheck, ExternalLink, Mail, FileText, BookOpen, Menu, X, Compass, AlertOctagon, Package, RefreshCw,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import CoachErrorBoundary from "@/components/coach/CoachErrorBoundary";
import { MemberCounterBadge } from "@/components/coach/MemberCounter";
import EasyAccess, { TodayPill } from "@/components/coach/EasyAccess";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// "New Package Soon" badge — families running low on pack sessions (or with
// open IOUs). Fetched by both the desktop sidebar and the mobile More sheet.
function usePackageSoonCount() {
    const [count, setCount] = useState(0);
    useEffect(() => {
        let cancelled = false;
        const tick = () => {
            axios.get(`${API}/coach/new-package-soon/count`, { withCredentials: true })
                .then((r) => { if (!cancelled) setCount(r.data?.count || 0); })
                .catch(() => {});
        };
        tick();
        const id = setInterval(tick, 5 * 60 * 1000);
        return () => { cancelled = true; clearInterval(id); };
    }, []);
    return count;
}

/**
 * Shared coach console layout. Renders the sticky left sidebar + an <Outlet />
 * for the active /coach/* route. Used to wrap every coach-side page so the
 * sidebar stays visible no matter which sub-page the coach navigates to.
 */
export default function CoachLayout() {
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();
    const [pendingCount, setPendingCount] = useState(0);
    const [unreadMessages, setUnreadMessages] = useState(0);
    const [mnpCount, setMnpCount] = useState(0);
    const [changeReqCount, setChangeReqCount] = useState(0);
    // Attendance health — red dot on the Today nav when bookings were
    // marked attended but the session deduction silently failed (no
    // usage_log entry). Sachin 2026-02-23. Data already exists in
    // /coach/attendance/today's counts.mismatches.
    const [mismatchCount, setMismatchCount] = useState(0);

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/", { replace: true }); return; }
        if (user.role !== "owner" && user.role !== "coach") {
            navigate("/dashboard", { replace: true });
        }
    }, [loading, user, navigate]);

    // Owner-only: pending assessment-request count for the sidebar badge
    useEffect(() => {
        if (user?.role !== "owner") return;
        let cancelled = false;
        axios.get(`${API}/coach/assessment-requests`, { withCredentials: true })
            .then((r) => { if (!cancelled) setPendingCount((r.data || []).length); })
            .catch(() => {});
        axios.get(`${API}/coach/mnp`, { withCredentials: true })
            .then((r) => { if (!cancelled) setMnpCount(r.data?.total || 0); })
            .catch(() => {});
        axios.get(`${API}/coach/subscription-change-requests?status=pending`, { withCredentials: true })
            .then((r) => { if (!cancelled) setChangeReqCount(r.data?.pending || 0); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [user]);

    // Unread message count (poll every 15s)
    useEffect(() => {
        if (!user || (user.role !== "owner" && user.role !== "coach")) return;
        let cancelled = false;
        const tick = () => {
            axios.get(`${API}/messages/unread-count`, { withCredentials: true })
                .then((r) => { if (!cancelled) setUnreadMessages(r.data?.unread || 0); })
                .catch(() => {});
        };
        tick();
        const id = setInterval(tick, 15000);
        return () => { cancelled = true; clearInterval(id); };
    }, [user]);

    // Attendance health (poll every 5 min — slow-moving signal)
    useEffect(() => {
        if (!user || (user.role !== "owner" && user.role !== "coach")) return;
        let cancelled = false;
        const tick = () => {
            axios.get(`${API}/coach/attendance/today`, { withCredentials: true })
                .then((r) => { if (!cancelled) setMismatchCount(r.data?.counts?.mismatches || 0); })
                .catch(() => {});
        };
        tick();
        const id = setInterval(tick, 5 * 60 * 1000);
        return () => { cancelled = true; clearInterval(id); };
    }, [user]);

    if (loading || !user) {
        return (
            <div className="min-h-screen bg-brand-obsidian flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-brand-red animate-spin" />
            </div>
        );
    }

    const isOwner = user.role === "owner";

    return (
        <div data-testid="coach-layout" className="bg-brand-obsidian min-h-screen">
            <TodayPill />
            <div className="md:flex md:pt-20">
                <CoachSidebar
                    isOwner={isOwner}
                    perms={user.permissions}
                    pendingCount={pendingCount}
                    mnpCount={mnpCount}
                    changeReqCount={changeReqCount}
                    unreadMessages={unreadMessages}
                    mismatchCount={mismatchCount}
                    userEmail={user.email}
                    onLogout={async () => { await logout(); navigate("/", { replace: true }); }}
                />
                <div className="flex-1 min-w-0 pb-20 md:pb-0">
                    <CoachErrorBoundary>
                        <Outlet />
                    </CoachErrorBoundary>
                </div>
            </div>
            <CoachMobileNav
                isOwner={isOwner}
                perms={user.permissions}
                pendingCount={pendingCount}
                mnpCount={mnpCount}
                changeReqCount={changeReqCount}
                unreadMessages={unreadMessages}
                mismatchCount={mismatchCount}
                onLogout={async () => { await logout(); navigate("/", { replace: true }); }}
            />
        </div>
    );
}


/**
 * CoachMobileNav — sticky bottom tab bar for the coach console on phones.
 * Mirrors the desktop sidebar's most-used links so coaches can switch
 * sections in one tap instead of opening the collapsible drawer. Hidden
 * on md+ where the full sidebar is always visible.
 *
 * 5 slots = max for a thumb-reachable bottom nav; we pick the highest-
 * traffic destinations and roll everything else under "More" (the
 * hamburger which already opens the existing sidebar drawer).
 */
function CoachMobileNav({ isOwner, perms, pendingCount, mnpCount, changeReqCount, unreadMessages, mismatchCount, onLogout }) {
    const inboxCount = (pendingCount || 0) + (mnpCount || 0) + (changeReqCount || 0);
    const can = (key) => isOwner || !!perms?.[key];
    const [moreOpen, setMoreOpen] = useState(false);
    const items = [
        ...(can("view_clients") ? [
            { to: "/coach", end: true, icon: Users, label: "Roster", badge: 0 },
        ] : []),
        // "Today" is the highest-frequency on-the-floor action — replaces
        // "Schedule" in the bottom bar (Schedule still reachable in the
        // EasyAccess block at the top of the More sheet).
        ...(can("view_attendance") ? [{ to: "/coach/today", icon: Calendar, label: "Today", badge: mismatchCount || 0, badgeKind: mismatchCount > 0 ? "warn" : null }] : []),
        ...(can("view_messages") ? [{ to: "/coach/messages", icon: MessageCircle, label: "Inbox", badge: unreadMessages }] : []),
        ...(can("view_clients") ? [{ to: "/coach/find", icon: Search, label: "Find", badge: 0 }] : []),
        ...(isOwner ? [{ to: "/coach/mnp", icon: UserPlus, label: "MNP", badge: inboxCount }] : []),
    ];
    const colsClass = ["", "grid-cols-1", "grid-cols-2", "grid-cols-3", "grid-cols-4", "grid-cols-5", "grid-cols-6"][items.length + 1] || "grid-cols-6";
    return (
        <>
        <nav
            data-testid="coach-mobile-nav"
            className="md:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-zinc-900 bg-black/95 backdrop-blur pb-[env(safe-area-inset-bottom)]"
        >
            <div className={`grid ${colsClass}`}>
                {items.map((l) => (
                    <NavLink
                        key={l.to}
                        to={l.to}
                        end={l.end}
                        data-testid={`coach-mobile-link-${l.label.toLowerCase()}`}
                        onClick={() => setMoreOpen(false)}
                        className={({ isActive }) => `relative flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] tracking-[0.18em] uppercase transition-colors ${
                            isActive && !moreOpen ? "text-brand-red" : "text-zinc-400 hover:text-white"
                        }`}
                    >
                        {({ isActive }) => (
                            <>
                                <div className="relative">
                                    <l.icon className="w-5 h-5" />
                                    {l.badge > 0 && (
                                        <span className={`absolute -top-1.5 -right-2.5 min-w-[16px] h-4 px-1 text-white text-[9px] font-bold tracking-normal rounded-full flex items-center justify-center ${l.badgeKind === "warn" ? "bg-amber-500" : "bg-brand-red"}`}
                                              data-testid={`coach-mobile-badge-${l.label.toLowerCase()}`}
                                              title={l.badgeKind === "warn" ? "Attendance health: unreconciled bookings need a look" : undefined}>
                                            {l.badge > 9 ? "9+" : l.badge}
                                        </span>
                                    )}
                                </div>
                                <span className="leading-none">{l.label}</span>
                                {isActive && !moreOpen && (
                                    <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-brand-red" />
                                )}
                            </>
                        )}
                    </NavLink>
                ))}
                <button
                    type="button"
                    data-testid="coach-mobile-link-more"
                    onClick={() => setMoreOpen((o) => !o)}
                    className={`relative flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] tracking-[0.18em] uppercase transition-colors ${
                        moreOpen ? "text-brand-red" : "text-zinc-400 hover:text-white"
                    }`}
                >
                    {moreOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    <span className="leading-none">More</span>
                    {moreOpen && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-brand-red" />}
                </button>
            </div>
        </nav>
        {moreOpen && (
            <CoachMobileMoreSheet
                isOwner={isOwner}
                can={can}
                pendingCount={pendingCount}
                mnpCount={mnpCount}
                changeReqCount={changeReqCount}
                unreadMessages={unreadMessages}
                onClose={() => setMoreOpen(false)}
                onLogout={onLogout}
            />
        )}
        </>
    );
}

/**
 * CoachMobileMoreSheet — full menu on phones. Renders the EXACT same
 * permission-gated link tree as the desktop sidebar (via buildCoachLinks)
 * so what a coach can reach on an iPad/desktop is identical on a phone.
 */
function CoachMobileMoreSheet({ isOwner, can, pendingCount, mnpCount, changeReqCount, unreadMessages, onClose, onLogout }) {
    const packageSoonCount = usePackageSoonCount();
    const links = buildCoachLinks({ isOwner, can, pendingCount, mnpCount, changeReqCount, unreadMessages, packageSoonCount });
    const itemClass = ({ isActive }) => `flex items-center gap-3 px-4 py-3 text-sm rounded-md transition-colors ${
        isActive ? "bg-brand-navy-900/50 text-white" : "text-zinc-200 hover:bg-zinc-900"
    }`;
    const renderLeaf = (c, idx) => c.external ? (
        <a
            key={c.href || idx}
            href={c.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`mobile-more-link-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
            className="flex items-center gap-3 px-4 py-3 text-sm rounded-md text-zinc-200 hover:bg-zinc-900"
        >
            <c.icon className="w-4 h-4 flex-shrink-0 text-zinc-400" />
            <span className="flex-1">{c.label}</span>
            <ExternalLink className="w-3 h-3 opacity-50" />
        </a>
    ) : (
        <NavLink
            key={c.to || idx}
            to={c.to}
            end={c.end}
            onClick={onClose}
            data-testid={`mobile-more-link-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={itemClass}
        >
            <c.icon className="w-4 h-4 flex-shrink-0 text-zinc-400" />
            <span className="flex-1">{c.label}</span>
            {!!c.badge && c.badge > 0 && (
                <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-[10px] font-bold rounded-full bg-brand-red text-white">
                    {c.badge}
                </span>
            )}
        </NavLink>
    );
    return (
        <div
            data-testid="coach-mobile-more-sheet"
            className="md:hidden fixed inset-0 z-30 bg-black/95 backdrop-blur overflow-y-auto pt-24 pb-24 px-3"
        >
            {/* Easy Access + Road-to-200 mirror what we show desktop coaches in
                the sidebar — same data, same buttons, same testids — so the
                gym-floor experience on a phone is no worse than on the desk. */}
            <div className="-mx-1 mb-2">
                <MemberCounterBadge onClick={onClose} />
            </div>
            <div className="-mx-1 mb-2 border border-zinc-900 rounded-md bg-zinc-950/40">
                <EasyAccess
                    collapsed={false}
                    canViewSchedule={isOwner || !!can("view_schedule")}
                    canViewAttendance={isOwner || !!can("view_attendance")}
                    canViewMessages={isOwner || !!can("view_messages")}
                    canAddLead={isOwner || !!can("view_clients")}
                    unreadMessages={unreadMessages}
                    onItemClick={onClose}
                />
            </div>
            <div className="my-2 border-t border-zinc-900" />
            {links.map((l, i) => {
                if (l.divider) return <div key={`div-${i}`} className="my-2 border-t border-zinc-900" />;
                if (l.group && Array.isArray(l.children)) {
                    return (
                        <div key={`grp-${l.group}`} className="mb-1">
                            <div className="px-4 pt-3 pb-1 text-[10px] tracking-[0.3em] uppercase text-zinc-500 flex items-center gap-2">
                                <l.icon className="w-3.5 h-3.5" /> {l.label}
                            </div>
                            {l.children.map(renderLeaf)}
                        </div>
                    );
                }
                return renderLeaf(l, i);
            })}
            <div className="my-2 border-t border-zinc-900" />
            <button
                type="button"
                data-testid="mobile-more-logout"
                onClick={onLogout}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-zinc-300 hover:text-brand-red rounded-md"
            >
                <LogOut className="w-4 h-4" /> Sign out
            </button>
        </div>
    );
}

/**
 * buildCoachLinks — the single source of truth for the coach console nav.
 * Used by BOTH the desktop/iPad sidebar and the phone "More" sheet so the
 * two always match. `can` = permission check (owner passes everything).
 */
function buildCoachLinks({ isOwner, can, pendingCount, mnpCount, changeReqCount, unreadMessages, mismatchCount = 0, packageSoonCount = 0 }) {
    // 2026-06 consolidation: 7 top-level items (Roster, Messages, Today,
    // People, Training, Money, Admin) replacing the old 12+ mixed list.
    // Every previous destination is preserved — just regrouped. Two pages
    // that had NO nav entry (Access audit, Unsettled IOUs) got homes.
    const requestsBadge = (pendingCount || 0) + (mnpCount || 0) + (changeReqCount || 0);

    const todayChildren = [
        ...(can("view_attendance") ? [
            { to: "/coach/today", icon: Calendar, label: "Today", badge: mismatchCount, badgeKind: mismatchCount > 0 ? "warn" : null },
        ] : []),
        ...(can("view_schedule") ? [{ to: "/coach/schedule", icon: Calendar, label: "Schedule" }] : []),
        ...(can("view_attendance") ? [
            { to: "/coach/attendance", icon: CheckCircle2, label: "Attendance" },
            { to: "/coach/check-in", icon: CheckCircle2, label: "Check-in" },
        ] : []),
        ...(can("view_clients") ? [{ to: "/coach/progress-updates", icon: TrendingUp, label: "Progress updates" }] : []),
    ];

    const peopleChildren = [
        ...(can("view_clients") ? [{ to: "/coach/find", icon: Search, label: "Find anyone" }] : []),
        ...(can("view_clients") ? [{ to: "/coach/new-package-soon", icon: Package, label: "New Package Soon", badge: packageSoonCount, badgeKind: packageSoonCount > 0 ? "warn" : null }] : []),
        ...(isOwner ? [{ to: "/coach/waivers", icon: FileText, label: "Waivers" }] : []),
        ...(can("run_assessments") ? [{ to: "/coach/assessments", icon: Activity, label: "Assessments" }] : []),
        ...(isOwner ? [
            { to: "/coach/assessment-requests", icon: ClipboardList, label: "Assessment requests", badge: pendingCount },
            { to: "/coach/mnp", icon: UserPlus, label: "MNP", badge: mnpCount },
            { to: "/coach/change-requests", icon: ArrowRightLeft, label: "Change requests", badge: changeReqCount },
            { to: "/coach/applications", icon: Briefcase, label: "Applications" },
            { to: "/coach/clubs", icon: Users, label: "Clubs" },
            { to: "/coach/coaches", icon: UserCog, label: "Coaches" },
        ] : []),
        { to: "/coach/contract", icon: FileText, label: "Contract" },
    ];

    const trainingChildren = [
        ...(can("manage_workouts") ? [{ to: "/coach/workouts", icon: Layers, label: "Workouts" }] : []),
        ...(can("manage_programs") ? [{ to: "/coach/programs", icon: Calendar, label: "Programs" }] : []),
        ...(can("manage_exercises") ? [{ to: "/coach/exercises", icon: Activity, label: "Exercises" }] : []),
        ...(can("manage_equipment") ? [{ to: "/coach/equipment", icon: Activity, label: "Equipment" }] : []),
        { to: "/coach/library", icon: BookOpen, label: "Library" },
        ...(can("manage_announcements") ? [{ to: "/coach/announcements", icon: Megaphone, label: "Announcements" }] : []),
    ];

    // Money — per-item gated: Revenue + Weekly metrics on `view_reports`,
    // Pricing blocks on `manage_pricing`; the sensitive rest stay owner-only.
    const moneyChildren = [
        ...(can("view_reports") ? [{ to: "/coach/revenue", icon: DollarSign, label: "Revenue dashboard" }] : []),
        ...(isOwner ? [{ to: "/coach/current-subscriptions", icon: CreditCard, label: "Current subscriptions" }] : []),
        ...(isOwner ? [{ to: "/coach/auto-renews", icon: RefreshCw, label: "Auto-renews" }] : []),
        ...(isOwner ? [{ to: "/coach/accounting", icon: Scale, label: "Accounting" }] : []),
        ...(can("manage_pricing") ? [{ to: "/coach/pricing-blocks", icon: Tag, label: "Pricing blocks" }] : []),
        ...(isOwner ? [{ to: "/coach/tax-liability", icon: Tag, label: "Tax liability" }] : []),
        ...(can("view_reports") ? [{ to: "/coach/metrics", icon: TrendingUp, label: "Weekly metrics" }] : []),
        ...(isOwner ? [{ to: "/coach/unsettled-ious", icon: Scale, label: "Unsettled IOUs" }] : []),
    ];

    return [
        ...(can("view_clients") ? [
            { to: "/coach", icon: Users, label: "Roster", end: true },
        ] : []),
        // Messages pinned high — Sachin 2026-02-28: was getting buried,
        // parents asking "where's chat?"
        ...(can("view_messages") ? [{ to: "/coach/messages", icon: MessageSquare, label: "Messages", badge: unreadMessages }] : []),
        ...(todayChildren.length > 0 ? [{
            // Today — everything that happens on the gym floor today.
            group: "today",
            icon: Calendar,
            label: "Today",
            badge: mismatchCount,
            children: todayChildren,
        }] : []),
        ...(peopleChildren.length > 0 ? [{
            // People — leads, members, staff, and the request queues.
            group: "people",
            icon: Users,
            label: "People",
            badge: requestsBadge,
            children: peopleChildren,
        }] : []),
        ...(trainingChildren.length > 0 ? [{
            // Training — authoring of workouts/programs/exercises and content.
            group: "training",
            icon: Layers,
            label: "Training",
            children: trainingChildren,
        }] : []),
        ...(moneyChildren.length > 0 ? [
            { divider: true },
            {
                // Money — every page that touches revenue, pricing, or tax.
                group: "money",
                icon: DollarSign,
                label: "Money",
                children: moneyChildren,
            },
        ] : []),
        ...(isOwner ? [
            ...(moneyChildren.length === 0 ? [{ divider: true }] : []),
            {
                // Admin — diagnostics, audit, system settings + external links.
                group: "admin",
                icon: UserCog,
                label: "Admin",
                children: [
                    { to: "/coach/activity", icon: Activity, label: "Activity log" },
                    { to: "/coach/chat-analytics", icon: MessageCircle, label: "Chat analytics" },
                    { to: "/coach/customer-journey", icon: Compass, label: "Customer journey" },
                    { to: "/coach/access-audit", icon: ShieldCheck, label: "Access audit" },
                    { to: "/coach/auth-health", icon: ShieldCheck, label: "Auth health" },
                    { to: "/coach/system-health", icon: AlertOctagon, label: "System health" },
                    { to: "/coach/coaches/contract-editor", icon: FileText, label: "Contract editor" },
                    {
                        href: "https://business.google.com/dashboard",
                        external: true,
                        icon: BarChart3,
                        label: "Business Profile",
                    },
                    {
                        href: "https://resend.com/emails",
                        external: true,
                        icon: Mail,
                        label: "Resend",
                    },
                ],
            },
        ] : []),
    ];
}

function CoachSidebar({ isOwner, perms, pendingCount, mnpCount, changeReqCount, unreadMessages, mismatchCount, userEmail, onLogout }) {
    const [collapsed, setCollapsed] = useState(false);
    const packageSoonCount = usePackageSoonCount();
    const location = useLocation();
    // Owner sees everything; coaches see only what's checked on their
    // Coaches-page checklist (perms = effective permissions from /auth/me).
    const can = (key) => isOwner || !!perms?.[key];
    // Persist per-group dropdown open/closed state in localStorage so the
    // section stays in whatever state the user left it in across reloads.
    const [openGroups, setOpenGroups] = useState(() => {
        try {
            const raw = window.localStorage.getItem("maxsc_sidebar_groups");
            return raw ? JSON.parse(raw) : {};
        } catch { return {}; }
    });
    const toggleGroup = (key) => setOpenGroups((prev) => {
        const next = { ...prev, [key]: !prev[key] };
        try { window.localStorage.setItem("maxsc_sidebar_groups", JSON.stringify(next)); } catch { /* noop */ }
        return next;
    });
    const links = buildCoachLinks({ isOwner, can, pendingCount, mnpCount, changeReqCount, unreadMessages, mismatchCount, packageSoonCount });
    return (
        <aside
            data-testid="coach-sidebar"
            className={`hidden md:flex md:flex-col border-r border-zinc-900 bg-black sticky top-20 self-start transition-[width] duration-200 ${
                collapsed ? "w-16" : "w-56"
            }`}
            style={{ height: "calc(100vh - 5rem)" }}
        >
            <button
                type="button"
                onClick={() => setCollapsed((c) => !c)}
                aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                className="self-end m-2 p-1.5 text-zinc-400 hover:text-white"
            >
                {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
            <nav className="flex-1 overflow-y-auto px-2 pb-4">
                <EasyAccess
                    collapsed={collapsed}
                    canViewSchedule={isOwner || !!perms?.view_schedule}
                    canViewAttendance={isOwner || !!perms?.view_attendance}
                    canViewMessages={isOwner || !!perms?.view_messages}
                    canAddLead={isOwner || !!perms?.view_clients}
                    unreadMessages={unreadMessages}
                />
                <div className={collapsed ? "" : "border-t border-zinc-900 my-1"} />
                {links.map((l, i) => {
                    if (l.divider) {
                        return <div key={`div-${i}`} className="my-2 border-t border-zinc-900" />;
                    }
                    if (l.group && Array.isArray(l.children)) {
                        // Dropdown group — Revenue, etc.
                        const childPaths = l.children.map((c) => c.to);
                        const hasActiveChild = childPaths.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));
                        const explicitlyOpen = openGroups[l.group];
                        // Auto-open when on a child page; user can still toggle.
                        const isOpen = explicitlyOpen ?? hasActiveChild;
                        // Sum any child badges for the parent count when collapsed.
                        return (
                            <div key={`grp-${l.group}`}>
                                <button
                                    type="button"
                                    data-testid={`sidebar-group-${l.group}`}
                                    onClick={() => toggleGroup(l.group)}
                                    aria-expanded={isOpen}
                                    title={collapsed ? l.label : undefined}
                                    className={`relative w-full flex items-center gap-3 px-3 py-2.5 mb-1 text-sm rounded-md transition-colors ${
                                        hasActiveChild
                                            ? "bg-brand-navy-900/30 text-white"
                                            : "text-zinc-200 hover:bg-zinc-900 hover:text-white"
                                    } ${collapsed ? "justify-center" : ""}`}
                                >
                                    <l.icon className="w-4 h-4 flex-shrink-0" />
                                    {!collapsed && (
                                        <>
                                            <span className="truncate flex-1 text-left">{l.label}</span>
                                            {l.badge > 0 && (
                                                <span
                                                    data-testid={`sidebar-group-${l.group}-badge`}
                                                    className="ml-1 inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-[10px] font-bold rounded-full bg-brand-red text-white"
                                                >
                                                    {l.badge}
                                                </span>
                                            )}
                                            <ChevronDown
                                                className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
                                            />
                                        </>
                                    )}
                                    {collapsed && l.badge > 0 && (
                                        <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-brand-red" aria-hidden="true" />
                                    )}
                                </button>
                                {isOpen && !collapsed && (
                                    <div data-testid={`sidebar-group-${l.group}-children`} className="ml-2 pl-3 border-l border-zinc-800 mb-1 space-y-0.5">
                                        {l.children.map((c) => (
                                            c.external ? (
                                                <a
                                                    key={c.href}
                                                    href={c.href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-testid={`sidebar-link-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                                                    className="flex items-center gap-2.5 px-3 py-2 text-[13px] rounded-md transition-colors text-zinc-300 hover:bg-zinc-900 hover:text-white"
                                                >
                                                    <c.icon className="w-3.5 h-3.5 flex-shrink-0 text-zinc-400" />
                                                    <span className="truncate">{c.label}</span>
                                                </a>
                                            ) : (
                                                <NavLink
                                                    key={c.to}
                                                    to={c.to}
                                                    data-testid={`sidebar-link-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                                                    className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 text-[13px] rounded-md transition-colors ${
                                                        isActive
                                                            ? "bg-brand-navy-900/50 text-white border-l-2 border-brand-navy-500 -ml-[2px] pl-[10px]"
                                                            : "text-zinc-300 hover:bg-zinc-900 hover:text-white"
                                                    }`}
                                                >
                                                    <c.icon className="w-3.5 h-3.5 flex-shrink-0 text-zinc-400" />
                                                    <span className="truncate flex-1">{c.label}</span>
                                                    {c.badge > 0 && (
                                                        <span className={`inline-flex items-center justify-center min-w-4 h-4 px-1 text-[9px] font-bold rounded-full text-white ${c.badgeKind === "warn" ? "bg-amber-500" : "bg-brand-red"}`}
                                                              title={c.badgeKind === "warn" ? "Unreconciled attendance — check Today" : undefined}>
                                                            {c.badge}
                                                        </span>
                                                    )}
                                                </NavLink>
                                            )
                                        ))}
                                    </div>
                                )}
                                {/* When the sidebar is collapsed to icons only, show each child
                                    as a tiny stacked icon list directly under the parent so
                                    nothing becomes unreachable. */}
                                {collapsed && isOpen && (
                                    <div className="mb-1">
                                        {l.children.map((c) => (
                                            c.external ? (
                                                <a
                                                    key={c.href}
                                                    href={c.href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    title={c.label}
                                                    className="flex justify-center items-center px-3 py-2 mb-0.5 rounded-md text-zinc-300 hover:bg-zinc-900 hover:text-white"
                                                >
                                                    <c.icon className="w-3.5 h-3.5" />
                                                </a>
                                            ) : (
                                                <NavLink
                                                    key={c.to}
                                                    to={c.to}
                                                    data-testid={`sidebar-link-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                                                    title={c.label}
                                                    className={({ isActive }) => `flex justify-center items-center px-3 py-2 mb-0.5 rounded-md ${
                                                        isActive
                                                            ? "bg-brand-navy-900/50 text-white"
                                                            : "text-zinc-300 hover:bg-zinc-900 hover:text-white"
                                                    }`}
                                                >
                                                    <c.icon className="w-3.5 h-3.5" />
                                                </NavLink>
                                            )
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    }
                    if (l.external) {
                        return (
                            <a
                                key={l.href}
                                href={l.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`sidebar-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                title={collapsed ? `${l.label} (opens new tab)` : undefined}
                                className={`relative flex items-center gap-3 px-3 py-2.5 mb-1 text-sm rounded-md text-zinc-200 hover:bg-zinc-900 hover:text-white transition-colors ${collapsed ? "justify-center" : ""}`}
                            >
                                <l.icon className="w-4 h-4 flex-shrink-0" />
                                {!collapsed && (
                                    <>
                                        <span className="truncate">{l.label}</span>
                                        <ChevronRight className="w-3 h-3 ml-auto opacity-50" />
                                    </>
                                )}
                            </a>
                        );
                    }
                    return (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.end}
                            data-testid={`sidebar-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) => `relative flex items-center gap-3 px-3 py-2.5 mb-1 text-sm rounded-md transition-colors ${
                                isActive
                                    ? "bg-brand-navy-900/50 text-white border-l-2 border-brand-navy-500"
                                    : "text-zinc-200 hover:bg-zinc-900 hover:text-white"
                            } ${collapsed ? "justify-center" : ""}`}
                            title={collapsed ? l.label : undefined}
                        >
                            <l.icon className="w-4 h-4 flex-shrink-0" />
                            {!collapsed && <span className="truncate">{l.label}</span>}
                            {!!l.badge && l.badge > 0 && (
                                <span className={`bg-brand-red text-white text-[10px] font-bold rounded-full px-1.5 min-w-5 h-5 inline-flex items-center justify-center ${collapsed ? "absolute top-1 right-1" : "ml-auto"}`}>
                                    {l.badge}
                                </span>
                            )}
                        </NavLink>
                    );
                })}
            </nav>
            <div className="border-t border-zinc-900 p-2 space-y-1">
                {!collapsed && <MemberCounterBadge />}
                {!collapsed && userEmail && (
                    <div
                        data-testid="sidebar-compose-as"
                        title="Click any email on this dashboard to compose from this address in Gmail"
                        className="px-3 pt-1 pb-2"
                    >
                        <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-500">
                            Sending as
                        </div>
                        <div className="text-[11px] text-brand-navy-200 truncate flex items-center gap-1.5">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                            {userEmail}
                        </div>
                    </div>
                )}
                <button
                    type="button"
                    data-testid="sidebar-logout"
                    onClick={onLogout}
                    title={collapsed ? "Sign out" : undefined}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm text-zinc-300 hover:text-brand-red hover:bg-zinc-900 rounded-md ${collapsed ? "justify-center" : ""}`}
                >
                    <LogOut className="w-4 h-4 flex-shrink-0" />
                    {!collapsed && "Sign out"}
                </button>
            </div>
        </aside>
    );
}



